import * as Sentry from '@sentry/react';

const API_BASE = '/api';

/**
 * Structured API error with field-level validation details from DRF.
 * `body` contains the raw JSON response, e.g. { customerPhone: ["Enter a valid phone number."] }
 */
export class ApiError extends Error {
  status: number;
  body: Record<string, string[]>;

  constructor(status: number, body: Record<string, string[]>) {
    const firstMsg =
      Object.values(body).flat()[0] ||
      (body as Record<string, unknown>).detail ||
      `API error: ${status}`;
    super(String(firstMsg));
    this.status = status;
    this.body = body;
  }

  /** Get the first error message for a given field, or undefined. */
  fieldError(field: string): string | undefined {
    const msgs = this.body[field];
    return Array.isArray(msgs) ? msgs[0] : undefined;
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    const outcome = await refreshToken();
    if (outcome === 'refreshed') {
      const retryRes = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
        credentials: 'include',
      });
      if (!retryRes.ok) throw new Error(`API error: ${retryRes.status}`);
      if (retryRes.status === 204) return undefined as T;
      return retryRes.json();
    }
    throw new Error(refreshFailureMessage(outcome));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Notified when a refresh comes back 401 — the session is definitively over. */
let authExpiredHandler: (() => void) | null = null;

/** Register the callback that clears client-side auth state (see AuthProvider). */
export function setAuthExpiredHandler(handler: (() => void) | null): void {
  authExpiredHandler = handler;
}

/**
 * How a refresh attempt ended.
 *
 * Only `expired` is a verdict about the session; `unavailable` means we never
 * got an answer. Collapsing the two into a boolean is what made a dropped
 * connection indistinguishable from a finished session at the call sites.
 */
type RefreshOutcome = 'refreshed' | 'expired' | 'unavailable';

/** The refresh currently in flight, shared by every caller. */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Ask the backend for a fresh access cookie.
 *
 * Concurrent 401s share one request. Refresh tokens rotate, so a second POST
 * carrying the token the first one just rotated away is rejected — a page that
 * fires several requests at once would otherwise log itself out. The promise is
 * cleared as soon as it settles, so the next 401 starts a fresh attempt.
 */
function refreshToken(): Promise<RefreshOutcome> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<RefreshOutcome> => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (res.ok) return 'refreshed';
      // Only a 401 means the session is really over. A 429 or a 5xx is
      // transient — tearing the session down over one would log out a user
      // whose credentials are perfectly good.
      if (res.status === 401) {
        authExpiredHandler?.();
        return 'expired';
      }
      return 'unavailable';
    } catch (err) {
      // fetch rejects only on transport failure. Being offline is an expected
      // state rather than a fault, and reporting it would recreate on the
      // client exactly the noise this change removed on the server. Anything
      // else — DNS, TLS, CORS, a connection dropped mid-flight — is a real
      // signal, and silently returning false is how it used to go unnoticed.
      if (navigator.onLine) {
        Sentry.captureException(err, { level: 'warning', tags: { area: 'auth-refresh' } });
      }
      return 'unavailable';
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Message for a 401 we could not clear, honest about whether the session ended. */
function refreshFailureMessage(outcome: RefreshOutcome): string {
  return outcome === 'expired' ? 'Authentication expired' : 'Could not reach the server';
}

// --- Types ---

export interface Tenant {
  id: number;
  name: string;
  slug: string;
  description: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  phoneNumber: string;
  isWhatsapp: boolean;
  instagramHandle: string;
  photos: TenantPhoto[];
  rating: number;
  businessHours: {
    [day: string]: { open: string; close: string } | null;
  };
}

export interface TenantPhoto {
  id: number;
  image: string;
  order: number;
}

/** An upcoming salon-wide closure. Per-worker absences are never exposed publicly. */
export interface TenantTimeOff {
  /** yyyy-MM-dd, inclusive. */
  startDate: string;
  endDate: string;
  /** "HH:mm", or null for the whole day. */
  startTime: string | null;
  endTime: string | null;
}

export interface TenantDetail extends Tenant {
  services: Service[];
  staff: Staff[];
  reviewCount: number;
  /**
   * Days in the next 14 with no bookable capacity at all — the salon is shut, or
   * every worker who could serve you is away. Derived server-side, so it covers a
   * one-worker salon without revealing whose absence caused it.
   */
  unavailableDates: string[];
  /** Salon-wide closures in the next 60 days, for the "we're closed" banner. */
  timeOff: TenantTimeOff[];
}

export interface Service {
  id: number;
  name: string;
  price: number;
  duration: number;
}

/** A worker as shown publicly (drives the booking-flow staff selector). */
export interface Staff {
  id: number;
  name: string;
  photo: string | null;
  /** Ids of the services this worker performs. */
  serviceIds: number[];
}

export interface Customer {
  /** Null when the salon deleted this client; the booking (and its revenue) is kept. */
  id: string | null;
  name: string;
  phone: string;
}

export interface Review {
  id: number;
  rating: number;
  comment: string;
  customerName: string;
  createdAt: string;
}

export type AppointmentStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface Appointment {
  id: number;
  tenantId: number;
  tenantName: string;
  tenantSlug: string;
  /** Null once the salon deleted this service; the booking (and its revenue) is kept. */
  serviceId: number | null;
  /** Snapshot from booking time — authoritative, and present even after the service is deleted. */
  serviceName: string;
  servicePrice: number;
  serviceDuration: number;
  /** Assigned worker, if the salon uses staff. Null for salon-level bookings. */
  staffId: number | null;
  staffName: string | null;
  customer: Customer;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  hasReview: boolean;
}

// --- Public API ---

export function fetchTenantDetail(slug: string): Promise<TenantDetail> {
  return apiFetch(`/tenants/${slug}/`);
}

export function fetchAvailableSlots(
  slug: string,
  date: string,
  serviceId: number,
  /** A specific worker id, or 'any'/undefined for the union of all qualified workers. */
  staffId?: number | 'any',
): Promise<string[]> {
  const staffParam = staffId && staffId !== 'any' ? `&staff_id=${staffId}` : '';
  return apiFetch(
    `/tenants/${slug}/available-slots/?date=${date}&service_id=${serviceId}${staffParam}`,
  );
}

export function createAppointment(
  slug: string,
  data: {
    serviceId: number;
    startTime: string;
    /** Omit (or undefined) for "any available" worker. */
    staffId?: number;
  },
): Promise<Appointment> {
  return apiFetch(`/tenants/${slug}/appointments/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Reviews ---

export function fetchReviews(slug: string, page = 1): Promise<PaginatedResponse<Review>> {
  return apiFetch(`/tenants/${slug}/reviews/${page > 1 ? `?page=${page}` : ''}`);
}

export function submitReview(
  slug: string,
  data: { appointmentId: number; rating: number; comment: string },
): Promise<Review> {
  return apiFetch(`/tenants/${slug}/reviews/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function submitLead(data: {
  name: string;
  email: string;
  phone: string;
  businessName: string;
  captchaToken: string;
}): Promise<{ detail: string }> {
  return apiFetch('/leads/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Auth API ---

export interface CustomerSession {
  id: string;
  name: string;
  phone: string;
  phoneVerified: boolean;
  email: string;
}

export interface Session {
  role: 'owner' | 'staff' | 'customer' | 'unknown';
  emailVerified: boolean;
  /** False for social-login accounts (e.g. Google), which have no password to change. */
  hasPassword: boolean;
  /** Present for owners and workers — the salon they belong to. */
  tenant?: Tenant;
  /** Present for workers (role === 'staff') — who they are. */
  staff?: { id: number; name: string };
  customer?: CustomerSession;
}

export type OAuthProvider = 'google';

/** Body shape for an OAuth call — Google sends an ID token. */
function oauthBody(token: string): string {
  return JSON.stringify({ idToken: token });
}

export function login(identifier: string, password: string): Promise<Session> {
  return apiFetch<Session>('/auth/login/', {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
  });
}

export function register(data: {
  name: string;
  email: string;
  phone: string;
  password: string;
  captchaToken: string;
}): Promise<Session> {
  return apiFetch<Session>('/auth/register/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function oauthLogin(provider: OAuthProvider, token: string): Promise<Session> {
  return apiFetch<Session>(`/auth/oauth/${provider}/`, {
    method: 'POST',
    body: oauthBody(token),
  });
}

/** Fetch the current session, or null if not authenticated. */
export async function fetchSession(): Promise<Session | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/session/`, { credentials: 'include' });
    if (res.ok) return res.json();
    if (res.status === 401 && (await refreshToken()) === 'refreshed') {
      const retry = await fetch(`${API_BASE}/auth/session/`, { credentials: 'include' });
      return retry.ok ? retry.json() : null;
    }
    return null;
  } catch {
    return null;
  }
}

export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await apiFetch('/auth/change-password/', {
    method: 'POST',
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

/**
 * End the session server-side. Resolves to whether the server actually did it.
 *
 * The auth cookies are HttpOnly, so only the backend can clear them: if this
 * call fails, the session is still alive no matter what the UI shows. fetch
 * resolves normally on a 4xx/5xx, so the status has to be checked explicitly —
 * a bare `.catch()` here would report a throttled or failed logout as success.
 */
export async function logout(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/logout/`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) {
      // A session that would not end is worth knowing about: the cookies are
      // still live and only the backend can clear them. Rare enough to report
      // every time.
      Sentry.captureMessage(`Logout rejected with ${res.status}`, {
        level: 'warning',
        tags: { area: 'auth-logout' },
      });
    }
    return res.ok;
  } catch (err) {
    if (navigator.onLine) {
      Sentry.captureException(err, { level: 'warning', tags: { area: 'auth-logout' } });
    }
    return false;
  }
}

// --- Email verification ---

export async function sendEmailCode(email?: string): Promise<void> {
  await apiFetch('/account/email/send-code/', {
    method: 'POST',
    body: JSON.stringify(email ? { email } : {}),
  });
}

export function verifyEmailCode(code: string): Promise<Session> {
  return apiFetch<Session>('/account/email/verify/', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// --- Password reset ---

export async function requestPasswordReset(email: string): Promise<void> {
  await apiFetch('/auth/password/reset-request/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await apiFetch('/auth/password/reset-confirm/', {
    method: 'POST',
    body: JSON.stringify({ email, code, newPassword }),
  });
}

// --- Customer profile & social accounts ---

export function updateCustomerProfile(
  data: Partial<{ name: string; phone: string }>,
): Promise<CustomerSession> {
  return apiFetch('/account/profile/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function fetchMyAppointments(): Promise<Appointment[]> {
  return apiFetch('/account/appointments/');
}

export function cancelAppointment(id: number): Promise<Appointment> {
  return apiFetch(`/account/appointments/${id}/cancel/`, { method: 'POST' });
}

// --- Admin API ---

export function fetchAdminAppointments(): Promise<Appointment[]> {
  return apiFetch('/admin/appointments/');
}

export function createAdminAppointment(data: {
  serviceId: number;
  customerName: string;
  customerPhone?: string;
  startTime: string;
  status: string;
  /** Optional assigned worker (salons with staff). */
  staffId?: number | null;
  /** Existing walk-in the owner picked from suggestions; omit to file a new one. */
  clientId?: string | null;
}): Promise<Appointment> {
  return apiFetch('/admin/appointments/', {
    method: 'POST',
    body: JSON.stringify({
      serviceId: data.serviceId,
      customerName: data.customerName,
      customerPhone: data.customerPhone ?? '',
      startTime: data.startTime,
      status: data.status,
      ...(data.staffId != null ? { staffId: data.staffId } : {}),
      ...(data.clientId ? { clientId: data.clientId } : {}),
    }),
  });
}

export function updateAppointment(
  id: number,
  data: Partial<{ status: string; startTime: string; staffId: number | null }>,
): Promise<Appointment> {
  return apiFetch(`/admin/appointments/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// --- Admin Analytics API ---

export type AnalyticsPeriod = 'week' | 'month' | 'quarter';

/** Inclusive date range, `yyyy-MM-dd` in salon-local time. */
export interface AnalyticsRange {
  start: string;
  end: string;
}

/**
 * Everything the Analytics tab shows, computed server-side for one period.
 * `trend` fields are a percent change vs the previous period, or null when
 * that period had nothing to compare against.
 */
export interface Analytics {
  period: AnalyticsPeriod;
  periodLabel: string;
  range: AnalyticsRange;
  previousRange: AnalyticsRange;
  revenue: {
    today: number;
    period: number;
    previous: number;
    trend: number | null;
    avgPerAppointment: number;
  };
  appointments: {
    total: number;
    statusBreakdown: Record<AppointmentStatus, number>;
    completionRate: number;
    mostPopularService: string;
  };
  clients: {
    unique: number;
    previousUnique: number;
    trend: number | null;
    new: number;
    returning: number;
  };
  charts: {
    /** One entry per day of the period; `label` is pre-formatted for the axis. */
    revenueTrend: { date: string; label: string; revenue: number }[];
    appointmentsByService: { name: string; count: number }[];
    statusDistribution: { name: AppointmentStatus; value: number }[];
  };
}

export function fetchAdminAnalytics(period: AnalyticsPeriod): Promise<Analytics> {
  return apiFetch(`/admin/analytics/?period=${period}`);
}

// --- Admin Services API ---

export function fetchAdminServices(): Promise<Service[]> {
  return apiFetch('/admin/services/');
}

export function createAdminService(data: {
  name: string;
  price: number;
  duration: number;
}): Promise<Service> {
  return apiFetch('/admin/services/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateAdminService(
  id: number,
  data: Partial<{ name: string; price: number; duration: number }>,
): Promise<Service> {
  return apiFetch(`/admin/services/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteAdminService(id: number): Promise<void> {
  return apiFetch(`/admin/services/${id}/`, { method: 'DELETE' });
}

// --- Admin Profile API ---

export function fetchAdminProfile(): Promise<Tenant> {
  return apiFetch('/admin/profile/');
}

export interface AdminAccount {
  username: string;
  email: string;
}

export function fetchAdminAccount(): Promise<AdminAccount> {
  return apiFetch('/admin/account/');
}

export function updateAdminProfile(
  data: Partial<{
    phoneNumber: string;
    isWhatsapp: boolean;
    instagramHandle: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
  }>,
): Promise<Tenant> {
  return apiFetch('/admin/profile/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// --- Admin Business Hours API ---

export type BusinessHours = { [day: string]: { open: string; close: string } | null };

export function fetchBusinessHours(): Promise<{ businessHours: BusinessHours }> {
  return apiFetch('/admin/business-hours/');
}

// --- Admin Photos API ---

async function apiUpload<T>(path: string, formData: FormData, method = 'POST'): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    body: formData,
    credentials: 'include',
  });

  if (res.status === 401) {
    const outcome = await refreshToken();
    if (outcome === 'refreshed') {
      const retryRes = await fetch(`${API_BASE}${path}`, {
        method,
        body: formData,
        credentials: 'include',
      });
      if (!retryRes.ok) {
        const body = await retryRes.json().catch(() => ({}));
        throw new ApiError(retryRes.status, body);
      }
      return retryRes.json();
    }
    throw new Error(refreshFailureMessage(outcome));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  return res.json();
}

export function fetchAdminPhotos(): Promise<TenantPhoto[]> {
  return apiFetch('/admin/photos/');
}

export function uploadAdminPhoto(file: File): Promise<TenantPhoto> {
  const formData = new FormData();
  formData.append('image', file);
  return apiUpload('/admin/photos/', formData);
}

export function deleteAdminPhoto(id: number): Promise<void> {
  return apiFetch(`/admin/photos/${id}/`, { method: 'DELETE' });
}

export function reorderAdminPhotos(orderedIds: number[]): Promise<void> {
  return apiFetch('/admin/photos/reorder/', {
    method: 'PUT',
    body: JSON.stringify({ orderedIds }),
  });
}

export function updateBusinessHours(
  businessHours: BusinessHours,
): Promise<{ businessHours: BusinessHours }> {
  return apiFetch('/admin/business-hours/', {
    method: 'PUT',
    body: JSON.stringify({ businessHours }),
  });
}

// --- Admin Staff API (owner) ---

/** "none": no account yet; "invited": awaiting password; "active": can log in. */
export type StaffInviteStatus = 'none' | 'invited' | 'active';

/** A worker as managed by the owner. `businessHours` is empty to inherit salon hours. */
export interface AdminStaff {
  id: number;
  name: string;
  email: string;
  serviceIds: number[];
  businessHours: BusinessHours;
  isActive: boolean;
  photo: string | null;
  inviteStatus: StaffInviteStatus;
}

export interface AdminClient {
  id: string;
  name: string;
  phone: string;
  /** True when this record is linked to a Calendyz account, which makes it read-only. */
  isSocial: boolean;
  appointmentCount: number;
  lastAppointment: string | null;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function fetchAdminStaff(): Promise<AdminStaff[]> {
  return apiFetch('/admin/staff/');
}

export function createAdminStaff(data: {
  name: string;
  email?: string;
  serviceIds?: number[];
  businessHours?: BusinessHours;
  isActive?: boolean;
}): Promise<AdminStaff> {
  return apiFetch('/admin/staff/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateAdminStaff(
  id: number,
  data: Partial<{
    name: string;
    serviceIds: number[];
    businessHours: BusinessHours;
    isActive: boolean;
  }>,
): Promise<AdminStaff> {
  return apiFetch(`/admin/staff/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteAdminStaff(id: number): Promise<void> {
  return apiFetch(`/admin/staff/${id}/`, { method: 'DELETE' });
}

export function uploadStaffPhoto(id: number, file: File): Promise<AdminStaff> {
  const formData = new FormData();
  formData.append('image', file);
  return apiUpload(`/admin/staff/${id}/photo/`, formData, 'PUT');
}

export function deleteStaffPhoto(id: number): Promise<void> {
  return apiFetch(`/admin/staff/${id}/photo/`, { method: 'DELETE' });
}

export function resendStaffInvite(id: number, email?: string): Promise<AdminStaff> {
  return apiFetch(`/admin/staff/${id}/resend-invite/`, {
    method: 'POST',
    body: JSON.stringify(email ? { email } : {}),
  });
}

// --- Admin Time Off API (owner) ---

/**
 * A one-off absence: the whole salon, or one worker, is away for a date range.
 *
 * It is a single continuous span, not a daily repeat. It runs from `startTime` on
 * `startDate` (midnight if null) through `endTime` on `endDate` (end of day if
 * null), so "10 Aug 14:00 -> 12 Aug 09:00" means the 11th is entirely off.
 *
 * Recurring weekly closures are `businessHours`, not this.
 */
export interface TimeOff {
  id: number;
  /** Null = the whole salon is closed; a worker id = only that worker. */
  staffId: number | null;
  staffName: string | null;
  /** yyyy-MM-dd, inclusive. */
  startDate: string;
  endDate: string;
  /** "HH:mm", or null for "from the start of startDate". */
  startTime: string | null;
  /** "HH:mm", or null for "through the end of endDate". */
  endTime: string | null;
  /** Private to the salon — never shown to customers. */
  note: string;
  /**
   * Bookings that now fall inside this block.
   *
   * Present only on create/update, where the owner has to decide about them. The
   * list endpoint omits the key rather than sending `[]`, so absent means "not
   * computed", never "none affected".
   */
  conflicts?: Appointment[];
  /**
   * Bookings this block had cancelled that it no longer covers — because the
   * update just shrank it, or because `fetchAdminTimeOffRestorable` asked what
   * removing it entirely would bring back. Same presence rule as `conflicts`.
   */
  restorable?: Appointment[];
}

export interface CancelConflictsResult {
  cancelled: number;
  /**
   * Everyone whose booking was cancelled, with a number to call.
   *
   * Cancelling notifies nobody — Calendyz sends no appointment emails or
   * messages at all — so this is the whole list, not the leftovers.
   */
  toContact: { name: string; phone: string; startTime: string }[];
}

export interface RestoreResult {
  restored: Appointment[];
  /**
   * The ones that couldn't come back, each with why — the slot was taken while
   * it was cancelled, or another block still covers it.
   */
  skipped: { id: number; name: string; startTime: string; reason: string }[];
}

export type TimeOffInput = {
  staffId?: number | null;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  note?: string;
};

export function fetchAdminTimeOff(): Promise<TimeOff[]> {
  return apiFetch('/admin/time-off/');
}

export function createAdminTimeOff(data: TimeOffInput): Promise<TimeOff> {
  return apiFetch('/admin/time-off/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateAdminTimeOff(id: number, data: Partial<TimeOffInput>): Promise<TimeOff> {
  return apiFetch(`/admin/time-off/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * What deleting this block would bring back, asked before anything is deleted.
 *
 * The block is still standing while the owner reads the answer, so the times it
 * covers stay closed — nobody can take a slot out from under a booking that is
 * about to be offered back.
 */
export function fetchAdminTimeOffRestorable(id: number): Promise<TimeOff> {
  return apiFetch(`/admin/time-off/${id}/?restorable=1`);
}

/**
 * Delete a block, optionally bringing back the bookings it had cancelled.
 *
 * `restore` carries the owner's answer to `fetchAdminTimeOffRestorable`, and
 * travels *with* the delete rather than after it: the link identifying these as
 * the salon's cancellations dies with the block, so a follow-up request would
 * have nothing left to prove them by. Some may still fail to come back — the
 * result says which and why.
 */
export function deleteAdminTimeOff(id: number, restore = false): Promise<RestoreResult> {
  return apiFetch(`/admin/time-off/${id}/${restore ? '?restore=true' : ''}`, { method: 'DELETE' });
}

/**
 * Cancel bookings stranded by time off.
 *
 * Notifies nobody — the response carries everyone the owner now has to call.
 * Ids are explicit so the owner cancels exactly what they saw. `timeOffId`
 * records which block they were cancelled for, so lifting it can offer them back.
 */
export function cancelTimeOffAppointments(data: {
  appointmentIds: number[];
  timeOffId?: number | null;
}): Promise<CancelConflictsResult> {
  return apiFetch('/admin/time-off/cancel-appointments/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Un-cancel bookings a lifted block had stranded.
 *
 * Each is re-checked against live availability first — the slot was public the
 * whole time it sat cancelled — so some may come back `skipped`.
 */
export function restoreTimeOffAppointments(data: {
  appointmentIds: number[];
}): Promise<RestoreResult> {
  return apiFetch('/admin/time-off/restore-appointments/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Worker (staff role) API ---

export function fetchStaffAppointments(): Promise<Appointment[]> {
  return apiFetch('/staff/appointments/');
}

export function updateStaffAppointment(
  id: number,
  data: { status: string },
): Promise<{ id: number; status: string }> {
  return apiFetch(`/staff/appointments/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// --- Admin Clients API (owner) ---

export function fetchAdminClients(
  search?: string,
  page?: number,
  walkInOnly?: boolean,
): Promise<PaginatedResponse<AdminClient>> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (page && page > 1) params.set('page', String(page));
  if (walkInOnly) params.set('walk_in', 'true');
  const qs = params.toString();
  return apiFetch(`/admin/clients/${qs ? `?${qs}` : ''}`);
}

export function createAdminClient(data: { name: string; phone?: string }): Promise<AdminClient> {
  return apiFetch('/admin/clients/', {
    method: 'POST',
    body: JSON.stringify({ ...data, phone: data.phone ?? '' }),
  });
}

export function updateAdminClient(
  id: string,
  data: { name?: string; phone?: string | undefined },
): Promise<AdminClient> {
  // `phone: undefined` means "cleared" here, but JSON.stringify would drop the
  // key and DRF's partial update would keep the old number. Send "" instead.
  const body: Record<string, string> = {};
  if (data.name !== undefined) body.name = data.name;
  if ('phone' in data) body.phone = data.phone ?? '';
  return apiFetch(`/admin/clients/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteAdminClient(id: string): Promise<void> {
  return apiFetch(`/admin/clients/${id}/`, { method: 'DELETE' });
}
